import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI FAQ hub index (EN mirror)
import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndHreflangEn, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const CAT_LABEL_EN: Record<string, string> = {
  general: "General", cost: "Pricing & Fees", process: "Process & Timing",
  docs: "Document Types", validity: "Validity", quality: "Quality & Verification",
  delivery: "Delivery & Shipping", authority: "Authorities & Visas",
};
const ORDER = ["general", "authority", "docs", "cost", "process", "quality", "delivery", "validity"];

export const Route = createFileRoute("/en/translate/naati/faq/")({
    loader: async () => {
    const { NAATI_FAQS } = await import("@/data/naati-faq-bank");
    return { __d: { NAATI_FAQS } };
  },
head: ({ loaderData }) => {
    const { NAATI_FAQS } = loaderData!.__d as typeof import("@/data/naati-faq-bank");
    const url = `${SITE}/en/translate/naati/faq`;
    const title = `NAATI Translation FAQ — ${NAATI_FAQS.length} Answers From NYC's Certified Team`;
    const desc = `${NAATI_FAQS.length} answers on NAATI certified translation: stamp verification, turnaround, visa and government use. Free document review before you file.`;

    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/faq/index", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/faq/index", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/faq/index", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/faq/index", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: canonicalAndHreflangEn("/en/translate/naati/faq", "/translate/naati/faq"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
          mainEntity: NAATI_FAQS.map((f) => ({
            "@type": "Question", name: f.questionEn,
            acceptedAnswer: { "@type": "Answer", text: f.shortAnswerEn },
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "NAATI Translation", item: `${SITE}/en/translate/naati` },
            { "@type": "ListItem", position: 3, name: "FAQ", item: url },
          ],
        }) },
      ],
    };
  },
  component: FaqHubEn,
});

function FaqHubEn() {
  const { NAATI_FAQS } = (Route.useLoaderData() as { __d: typeof import("@/data/naati-faq-bank") }).__d;
  const grouped = ORDER.map((cat) => ({
    cat, label: CAT_LABEL_EN[cat],
    items: NAATI_FAQS.filter((f) => f.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/en" className="hover:underline">Home</Link> ·{" "}
        <Link to="/en/translate/naati" className="hover:underline">NAATI Translation</Link> ·{" "}
        <span className="text-slate-700">FAQ</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">NAATI Translation FAQ</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        The {NAATI_FAQS.length} questions Thai-Australian clients ask NYC most often — from &ldquo;what colour is a NAATI stamp?&rdquo;
        to &ldquo;which documents does subclass 189 need?&rdquo; Every answer is written from real casework, not AI boilerplate.
      </p>
      {grouped.map((g) => (
        <section key={g.cat} className="mb-10">
          <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
            {g.label} <span className="text-sm font-normal text-slate-500">({g.items.length})</span>
          </h2>
          <ul className="grid md:grid-cols-2 gap-3">
            {g.items.map((f) => (
              <li key={f.slug}>
                <Link
                  to="/en/translate/naati/faq/$slug" params={{ slug: f.slug }}
                  className="block border rounded-lg p-4 hover:border-gold hover:bg-amber-50/50 transition"
                >
                  <div className="font-medium text-navy-deep">{f.questionEn}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">{f.shortAnswerEn}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <div className="mt-8 p-6 bg-navy-deep text-white rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Question not listed?</h2>
        <p>Chat LINE @nycli or open the <Link to="/en/translate/naati" className="underline text-gold">NAATI service page</Link> — we reply within 15 minutes during Bangkok hours.</p>
      </div>
      <NaatiClusterLinks />
    </main>
  );
}
